/**
 * PartySheetScene — transparent overlay that hosts PokemonFullSheet for
 * the orb-tap inspection path.
 *
 * Critical lifecycle rule: this scene does NOT call scene.sleep on
 * WorldScene. It launches as a TRANSPARENT OVERLAY so the world stays
 * visible behind the sheet — the chrome IS the party / always there
 * (design review pass 1 decision; the lifecycle contradiction was
 * caught during eng review and fixed by this contract).
 *
 * Coordination with WorldScene:
 *   - On launch: WorldScene.setModalOverlayOpen(true, 'partySheet') is
 *     called BEFORE scene.launch, which adds 'partySheet' to the open-
 *     sources Set and (a) dims orbs to 50%, (b) disables joystick
 *     pointer-events, (c) gates tryProcessMovement.
 *   - On close: WorldScene.setModalOverlayOpen(false, 'partySheet'),
 *     then scene.stop() (no wake — WorldScene was never asleep).
 *
 * Render pattern (eng review Issue 1 — async-create):
 *   - create() launches sync, draws the modal panel + a skeleton
 *     immediately so the user sees the sheet open without a blank
 *     window.
 *   - partyMemberToOffer(member) resolves the dex chunk (eager-warmed
 *     at boot → microsecond cache hit typically), then the real content
 *     patches in via PokemonFullSheet construction.
 *
 * CTA = 'set-as-lead' → onTake dispatches:
 *   runState.reorderParty(currentIndex, 0) + saveManager.save() + close
 *
 * The sequenced "Set as lead" choreography (sheet fades 200ms, then orb
 * slides slot N → slot 0 over 280ms cubic-out, growing 52→60 px en
 * route) is partly here (sheet fade-out) + partly in PartyOrbs (orb
 * slide). The orb-slide piece lands with the broader PartyOrbs
 * reactivity in step 9 — for step 4, the order commits via reorderParty
 * and the next render reflects the new positions. The fade is local
 * here, immediate visual feedback regardless.
 */
import Phaser from 'phaser';
import { runState } from '../state/runState';
import { saveManager } from '../state/saveManager';
import { partyMemberToOffer } from '../state/offerHelpers';
import { PokemonFullSheet, type FullSheetCta } from './PokemonFullSheet';
import { effectsReduced } from '../render/motion';

export interface PartySheetSceneData {
  /** Index into runState.party at launch time. PartySheetScene captures
   *  the member by id at create() so a mid-sheet reorder (e.g. a player
   *  triple-tapping orbs) can't dispatch the "set as lead" against the
   *  wrong member. */
  partyIndex: number;
}

export class PartySheetScene extends Phaser.Scene {
  private params!: PartySheetSceneData;
  private memberId: string | null = null;
  private sheet: PokemonFullSheet | null = null;
  /** DOM-overlay loading skeleton — reuses PokemonFullSheet's
   *  `.dossier-modal` + `.dossier-card` classes so the skeleton's panel
   *  matches the eventual real-sheet panel pixel-for-pixel. Mount/
   *  destroy happens in the same JS event-loop tick as the real sheet's
   *  render so the transition is a single-frame swap, not a double-mount
   *  flicker. */
  private skeleton: HTMLDivElement | null = null;

  constructor() {
    super('PartySheetScene');
  }

  init(data: PartySheetSceneData): void {
    this.params = data;
    this.memberId = null;
    this.sheet = null;
    this.skeleton = null;
    // Defensive teardown — if the scene is stopped without going through
    // close() (e.g. a parent SHUTDOWN cascade), the skeleton DOM panel
    // would otherwise outlive the scene. PokemonFullSheet has its own
    // SHUTDOWN handler in its constructor; this one covers the skeleton
    // mount + any sheet that hasn't fully constructed yet.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.skeleton?.remove();
      this.skeleton = null;
    });
  }

  async create(): Promise<void> {
    const member = runState.getParty()[this.params.partyIndex];
    if (!member) {
      // Stale index (party shrank between tap and create). Close
      // immediately — same UX as opening then finding nothing to show.
      this.close();
      return;
    }
    // Capture by id, not by index — the party array may shift between
    // create() and the user tapping "Set as lead" (e.g. a faint event
    // mid-sheet, though unlikely with no battle running). reorderParty
    // dispatches against the live index lookup at click time.
    this.memberId = member.id;

    // Skeleton: glass-fill-mid panel + "…" placeholder while the dex
    // chunk resolves. With a warm cache this disappears on the next
    // frame; cold cache shows it for ~100 ms.
    this.skeleton = this.renderSkeleton();

    let offer;
    try {
      offer = await partyMemberToOffer(member);
    } catch (err) {
      console.warn('[PartySheet] partyMemberToOffer failed:', err);
      this.close();
      return;
    }

    // The user may have closed the sheet (via the WorldScene flag flip
    // mid-await) before the dex resolved. Defensive: bail if we're no
    // longer the active scene.
    if (!this.scene.isActive()) return;
    // Same defensive check on the live member — Reset Run or a faint
    // could have removed the slot we were inspecting.
    if (!runState.getParty().some((m) => m.id === this.memberId)) {
      this.close();
      return;
    }

    // Tear down skeleton + immediately mount the real sheet in the
    // same JS event-loop tick. Both elements use `.dossier-modal` /
    // `.dossier-card` so the panel geometry is identical — the user
    // sees content swap inside the same panel rectangle, no flicker.
    this.skeleton?.remove();
    this.skeleton = null;

    // DR-4A: hide "Set as lead" CTA when the inspected member IS the
    // current lead (slot 0). Compares by id so concurrent reorders don't
    // false-positive against a stale index.
    const isLead = runState.getParty()[0]?.id === this.memberId;
    const cta: FullSheetCta = isLead ? 'back-only' : 'set-as-lead';

    this.sheet = new PokemonFullSheet(
      this,
      { kind: 'live', offer, member },
      {
        context: 'live-context',
        cta,
        onBack: () => this.close(),
        onTake: isLead ? undefined : () => this.commitSetAsLead(),
      },
    );
  }

  /** Build the loading-state skeleton: a DOM panel that reuses
   *  PokemonFullSheet's `.dossier-modal` + `.dossier-card` classes so
   *  the panel material (glass-fill-thick backdrop + glass-fill-mid
   *  card + 12px radius + entry animation) is identical to the eventual
   *  real sheet. The transition is a single-tick destroy+mount swap.
   *
   *  Per DESIGN.md "Skeleton panel" component vocab — added by the
   *  2026-05-13 /plan-design-review of the Kanto-cleanup work. */
  private renderSkeleton(): HTMLDivElement {
    const gameEl = document.getElementById('game') ?? document.body;

    const root = document.createElement('div');
    root.className = 'dossier-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-busy', 'true');
    root.setAttribute('aria-label', 'Loading Pokémon sheet');
    // Tap on the backdrop closes (same affordance as the real Back btn).
    root.addEventListener('click', (e) => {
      if (e.target === root) this.close();
    });

    const card = document.createElement('div');
    card.className = 'dossier-card';
    card.addEventListener('click', (e) => e.stopPropagation());

    const placeholder = document.createElement('div');
    placeholder.textContent = '…';
    placeholder.style.fontFamily =
      "'DM Mono', ui-monospace, 'SF Mono', monospace";
    placeholder.style.fontSize = '28px';
    placeholder.style.fontWeight = '500';
    placeholder.style.color = 'var(--text-tertiary)';
    placeholder.style.textAlign = 'center';
    placeholder.style.padding = '80px 0';
    placeholder.style.lineHeight = '1';

    card.appendChild(placeholder);
    root.appendChild(card);
    gameEl.appendChild(root);

    return root;
  }

  /** Sequenced "Set as lead" handler. Per design doc:
   *    (1) Sheet fades + scales to 0.95 over 200 ms.
   *    (2) After the sheet is gone: reorderParty + save. The orb-side
   *        slide (52→60 px + slot N → slot 0 over 280 ms cubic-out)
   *        lands when PartyOrbs re-renders next — its standard
   *        re-render path covers the slot reflow. The visible
   *        choreography of fade-then-slide reads as cause-and-effect
   *        even without a tween bridge, because reorderParty fires
   *        AFTER the fade completes.
   *
   *  Under prefers-reduced-motion the fade collapses to 0 ms and the
   *  reorder dispatches immediately (no visible animation, snap commit). */
  private commitSetAsLead(): void {
    if (this.memberId === null) {
      this.close();
      return;
    }
    // Live index lookup — the slot may have shifted since create().
    const liveIndex = runState
      .getParty()
      .findIndex((m) => m.id === this.memberId);
    if (liveIndex < 0) {
      this.close();
      return;
    }

    const finalize = (): void => {
      runState.reorderParty(liveIndex, 0);
      saveManager.save();
      // Orchestrator (WorldScene.setModalOverlayOpen(false, 'partySheet'))
      // un-dims orbs + re-enables joystick. close() calls it.
      this.close();
    };

    if (effectsReduced()) {
      finalize();
      return;
    }
    // EN-5 / EN-5b: the legacy `this.tweens.add({ targets: this.children
    // .getAll() })` tween targeted PHASER scene children, but
    // PokemonFullSheet is a DOM overlay parented to #game — not a Phaser
    // child. The tween was a visible no-op (animating only the destroyed
    // skeleton). The fix switches to a CSS transition on the FullSheet
    // root DOM panel via the new getRootElement() accessor.
    const root = this.sheet?.getRootElement();
    if (!root) {
      finalize();
      return;
    }
    root.style.transition =
      'opacity 200ms cubic-bezier(0.55, 0, 0.55, 1),'
      + ' transform 200ms cubic-bezier(0.55, 0, 0.55, 1)';
    root.style.opacity = '0';
    root.style.transform = 'scale(0.95)';
    window.setTimeout(finalize, 200);
  }

  /** Close the sheet without committing anything. Both the Back button
   *  and the backdrop-tap dismiss path route here. The order matters:
   *  flip the WorldScene flag FIRST so orbs un-dim immediately, THEN
   *  scene.stop() so the sheet teardown is the last thing the player
   *  sees move. */
  private close(): void {
    const world = this.scene.get('WorldScene') as Phaser.Scene & {
      setModalOverlayOpen?: (open: boolean, source: 'partySheet') => void;
    };
    world.setModalOverlayOpen?.(false, 'partySheet');
    this.sheet?.destroy();
    this.sheet = null;
    this.skeleton?.remove();
    this.skeleton = null;
    this.scene.stop();
    // NO scene.wake('WorldScene') — World was never asleep. The transparent-
    // overlay contract is the load-bearing piece of this design.
  }
}
